// Folder utility functions — pure helpers with no side effects

// ── Types ───────────────────────────────────────────────────────

export interface FolderItem {
  id: string;
  type: "folder" | "asset";
  name: string;
  parentId: string | null;
}

export interface Breadcrumb {
  id: string | null;
  name: string;
}

// ── Breadcrumbs ─────────────────────────────────────────────────

/** Build the breadcrumb chain from root → current folder */
export function buildBreadcrumbs(folderId: string | null, items: FolderItem[]): Breadcrumb[] {
  const root: Breadcrumb = { id: null, name: "Assets" };
  if (folderId === null) return [root];

  const crumbs: Breadcrumb[] = [];
  let currentId: string | null = folderId;

  while (currentId !== null) {
    const item = items.find((i) => i.id === currentId);
    if (!item) break;
    crumbs.unshift({ id: item.id, name: item.name });
    currentId = item.parentId;
  }

  // If we couldn't resolve the chain (bad id), just return root
  if (crumbs.length === 0) return [root];

  return [root, ...crumbs];
}

// ── Filtering ───────────────────────────────────────────────────

/** Get direct children of a folder, folders sorted before assets */
export function getItemsInFolder(parentId: string | null, items: FolderItem[]): FolderItem[] {
  const children = items.filter((i) => i.parentId === parentId);
  // Sort: folders first, then assets, alphabetical within each group
  return children.sort((a, b) => {
    if (a.type !== b.type) return a.type === "folder" ? -1 : 1;
    return a.name.localeCompare(b.name);
  });
}

// ── Counting ────────────────────────────────────────────────────

/** Count direct children of a folder */
export function countFolderContents(parentId: string | null, items: FolderItem[]): number {
  return items.filter((i) => i.parentId === parentId).length;
}
